import { cn } from '@/lib/cn';

export type StatusVariant = 'healthy' | 'warning' | 'critical' | 'unknown';

interface StatusBadgeProps {
  status: StatusVariant;
  label?: string;
  size?: 'sm' | 'md';
  pulse?: boolean;
}

const variantStyles: Record<StatusVariant, { dot: string; text: string; bg: string }> = {
  healthy: {
    dot: 'bg-status-healthy shadow-[0_0_8px_rgba(16,185,129,0.6)]',
    text: 'text-status-healthy',
    bg: 'bg-status-healthy/10 border-status-healthy/20',
  },
  warning: {
    dot: 'bg-status-warning shadow-[0_0_8px_rgba(245,158,11,0.6)]',
    text: 'text-status-warning',
    bg: 'bg-status-warning/10 border-status-warning/20',
  },
  critical: {
    dot: 'bg-status-critical shadow-[0_0_8px_rgba(239,68,68,0.6)]',
    text: 'text-status-critical',
    bg: 'bg-status-critical/10 border-status-critical/20',
  },
  unknown: {
    dot: 'bg-status-unknown shadow-[0_0_6px_rgba(107,114,128,0.4)]',
    text: 'text-status-unknown',
    bg: 'bg-status-unknown/10 border-status-unknown/20',
  },
};

const defaultLabels: Record<StatusVariant, string> = {
  healthy: 'Healthy',
  warning: 'Warning',
  critical: 'Critical',
  unknown: 'Unknown',
};

export default function StatusBadge({ status, label, size = 'md', pulse = false }: StatusBadgeProps) {
  const styles = variantStyles[status];
  const displayLabel = label ?? defaultLabels[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 border rounded-full',
        styles.bg,
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm',
      )}
    >
      <span
        className={cn(
          'rounded-full flex-shrink-0',
          styles.dot,
          size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2',
          pulse && status === 'critical' && 'animate-pulse',
        )}
      />
      <span className={cn('font-medium', styles.text)}>
        {displayLabel}
      </span>
    </span>
  );
}

/**
 * Derive a status variant from service health data.
 */
export function deriveStatus(healthy: number, unhealthy: number, total: number): StatusVariant {
  if (total === 0) return 'unknown';
  if (unhealthy > 0) return 'critical';
  if (healthy < total) return 'warning';
  return 'healthy';
}
